import { useTranslation } from 'react-i18next'

import {
  Button,
  CircleRating,
  EditIcon,
  Tag,
  TagVariantEnum,
  Text,
} from '@mercai/clever'

import { useAuth } from '@/providers'
import { isEnabledCreditOrderTracker } from '@/helpers'

import { InfoCardProps } from './info-card.type'

import styles from './info-card.module.css'
import {
  FinanceProfileCreditStatusEnum,
  FinanceProfilePersonTypeEnum,
} from '@/types'
import { calculateFilledPercentage } from './info-card.constants'

export const InfoCard = ({ onEdit, profile }: InfoCardProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenProfileShow')

  const { whoAmI } = useAuth()

  const statusObjects: Record<
    FinanceProfileCreditStatusEnum,
    { label: string; variant: keyof typeof TagVariantEnum }
  > = {
    requested: {
      label: tEnums('profile_credit_status_requested'),
      variant: 'primary',
    },
    review: {
      label: tEnums('profile_credit_status_review'),
      variant: 'warning',
    },
    approved: {
      label: tEnums('profile_credit_status_approved'),
      variant: 'success',
    },
    rejected: {
      label: tEnums('profile_credit_status_rejected'),
      variant: 'danger',
    },
    non_applicant: {
      label: tEnums('profile_credit_status_non_applicant'),
      variant: 'primary',
    },
  }

  const statusObject =
    statusObjects[profile?.financeProfile?.creditStatus || 'non_applicant']

  const documentsPercentage = calculateFilledPercentage({
    addressProofFile: profile?.financeProfile?.addressProofFile,
    analysisReportFile: profile?.financeProfile?.analysisReportFile,
    constitutiveActFile:
      profile?.financeProfile?.personType === FinanceProfilePersonTypeEnum.legal
        ? profile?.financeProfile?.constitutiveActFile
        : 'file',
    governmentIdFile: profile?.financeProfile?.governmentIdFile,
    powerOfAttorneyFile: profile?.financeProfile?.powerOfAttorneyFile,
    promissoryNoteFile: profile?.financeProfile?.promissoryNoteFile,
    riskAssessmentReportFile: profile?.financeProfile?.riskAssessmentReportFile,
    siteInspectionReportFile: profile?.financeProfile?.siteInspectionReportFile,
  })

  return (
    <div className={styles.core}>
      <div className={styles.content}>
        <div>
          <Text variant="title-base" className={styles.label}>
            {t('profileCreditDocumentsLabel')}:
          </Text>
          <Text>
            <CircleRating rating={documentsPercentage} maxRating={100} />
          </Text>
        </div>
        <div>
          <Text variant="title-base" className={styles.label}>
            {t('profileCreditStatusLabel')}:
          </Text>
          <Text>
            <Tag model="light" variant={statusObject.variant}>
              {statusObject.label}
            </Tag>
          </Text>
        </div>
        <div>
          <Text variant="title-base" className={styles.label}>
            {t('profileCreditGatewayLabel')}:
          </Text>
          <Text>
            {profile?.financeProfile?.creditGateway
              ? tEnums(
                  `profile_credit_gateway_${profile?.financeProfile?.creditGateway}`,
                )
              : '-'}
          </Text>
        </div>
        <div>
          <Text variant="title-base" className={styles.label}>
            {t('profileCreditPersonTypeLabel')}:
          </Text>
          <Text>
            {profile?.financeProfile?.personType
              ? tEnums(
                  `profile_credit_person_type_${profile?.financeProfile?.personType}`,
                )
              : '-'}
          </Text>
        </div>
      </div>

      {isEnabledCreditOrderTracker(whoAmI?.email || '') && onEdit && (
        <Button onClick={onEdit} variant="secondary" model="square">
          <EditIcon />
        </Button>
      )}
    </div>
  )
}
